import { makeStyles } from '@fluentui/react-components';
import { useLocation } from 'react-router-dom';

import { headingForSection, isMonthAwareSection, sectionFromPath } from '../app/RouteMeta';
import { firstNameOf } from '../common';
import { useCurrentUser } from '../current-user';
import { useSettings } from '../settings';
import { palette, tokens } from '../theme';
import { MonthPicker } from './MonthPicker';
import { ProfileChip } from './ProfileChip';

export const Header: React.FC = () => {
    const classes = useStyles();
    const location = useLocation();
    const { user } = useCurrentUser();
    const firstName = user ? firstNameOf(user.name) : null;
    const { configured } = useSettings();

    const section = sectionFromPath(location.pathname);
    const showMonthNav = isMonthAwareSection(section) && configured;
    const heading = headingForSection(section, firstName);

    return (
        <header className={classes.root}>
            <div className={classes.leftSlot}>
                {heading && (
                    <div className={classes.headingBlock}>
                        <h1 className={classes.pageTitle}>{heading.title}</h1>
                        <p className={classes.pageSubtitle}>{heading.subtitle}</p>
                    </div>
                )}
            </div>
            <div className={classes.centerSlot}>{showMonthNav && <MonthPicker />}</div>
            <div className={classes.rightSlot}>{configured && <ProfileChip />}</div>
        </header>
    );
};

const useStyles = makeStyles({
    root: {
        display: 'grid',
        gridTemplateColumns: '1fr auto 1fr',
        alignItems: 'center',
        minHeight: '58px',
        padding: `13px ${tokens.spacingHorizontalL} 3px`,
        backgroundColor: palette.pageBg,
        flexShrink: 0,
    },
    leftSlot: {
        justifySelf: 'start',
        display: 'inline-flex',
        alignItems: 'center',
        gap: tokens.spacingHorizontalM,
        minWidth: 0,
    },
    centerSlot: {
        justifySelf: 'center',
        display: 'inline-flex',
        alignItems: 'center',
    },
    rightSlot: {
        justifySelf: 'end',
        display: 'inline-flex',
        alignItems: 'center',
        gap: tokens.spacingHorizontalM,
    },
    headingBlock: {
        display: 'flex',
        flexDirection: 'column',
        gap: '6px',
        padding: '0 10px',
        minWidth: 0,
    },
    pageTitle: {
        margin: 0,
        fontSize: '22px',
        fontWeight: tokens.fontWeightBold,
        lineHeight: 1.1,
        letterSpacing: '-0.2px',
        color: tokens.colorNeutralForeground1,
    },
    pageSubtitle: {
        margin: 0,
        fontSize: '13px',
        fontWeight: tokens.fontWeightRegular,
        color: palette.dimText,
        lineHeight: 1.3,
        '& em': {
            fontStyle: 'normal',
            color: palette.accent,
            fontWeight: tokens.fontWeightSemibold,
        },
    },
});
